import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import {
  Play,
  Pause,
  Eye,
  EyeOff,
  RotateCcw,
  Check,
  X,
  Smartphone,
  Maximize2,
  Loader2
} from 'lucide-react';
import { Area } from 'react-easy-crop/types';
import { cn } from "../../lib/utils";

// shadcn UI
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Separator } from "../ui/separator";
import { Label } from "../ui/label";

interface VideoCropperProps {
  open: boolean;
  onClose: () => void;
  onCropComplete: (
    cropData: { x: number; y: number; width: number; height: number; aspect: number },
    crop: { x: number; y: number },
    zoom: number
  ) => void;
  videoUrl: string;
  videoDuration?: number;
  startTime?: number;
  endTime?: number;
  initialCrop?: { x: number; y: number } | null;
  initialZoom?: number | null;
  initialAspect?: number | null;
  needsMobileFormat?: boolean;
}

const ASPECT_PRESETS = [
  { label: '9:16', value: 9 / 16 },
  { label: '4:5', value: 4 / 5 },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
];

const formatTime = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

export default function VideoCropper({
  open,
  onClose,
  onCropComplete,
  videoUrl,
  videoDuration,
  startTime,
  endTime,
  initialCrop,
  initialZoom,
  initialAspect,
  needsMobileFormat = true
}: VideoCropperProps): JSX.Element {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const cropperContainerRef = useRef<HTMLDivElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [detectedDuration, setDetectedDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVerticalFramer, setShowVerticalFramer] = useState(true);

  // Aspect ratio: number means we have it, null means waiting for video metadata
  const [selectedAspect, setSelectedAspect] = useState<number | null>(null);
  const [nativeAspect, setNativeAspect] = useState<number | null>(null);

  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const metadataDetectedRef = useRef(false);

  // Helper: find the video element inside the cropper container
  const findVideoEl = useCallback((): HTMLVideoElement | null => {
    if (videoElRef.current) return videoElRef.current;
    if (cropperContainerRef.current) {
      const el = cropperContainerRef.current.querySelector('video');
      if (el) {
        videoElRef.current = el;
        return el;
      }
    }
    return null;
  }, []);

  // Reset everything when dialog opens
  useEffect(() => {
    if (open) {
      setCrop(initialCrop || { x: 0, y: 0 });
      setZoom(initialZoom || 1);
      setCurrentTime(0);
      setIsPlaying(false);
      setDetectedDuration(0);
      setVideoReady(false);
      setSelectedAspect(null);
      setNativeAspect(null);
      videoElRef.current = null;
      metadataDetectedRef.current = false;
    }
  }, [open, videoUrl]);

  // Polling: detect video element, get metadata, track time
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);

    if (open) {
      pollRef.current = setInterval(() => {
        const vid = findVideoEl();
        if (!vid) return;

        // Detect native aspect from actual video dimensions (once)
        if (!metadataDetectedRef.current && vid.videoWidth > 0 && vid.videoHeight > 0) {
          metadataDetectedRef.current = true;
          const native = vid.videoWidth / vid.videoHeight;
          setNativeAspect(native);

          // If reopening with a saved crop, restore saved aspect; otherwise use native
          if (initialAspect && initialCrop) {
            setSelectedAspect(initialAspect);
          } else {
            setSelectedAspect(native);
          }
          setVideoReady(true);
        }

        // Track duration
        if (vid.duration && !isNaN(vid.duration) && vid.duration > 0) {
          setDetectedDuration(vid.duration);
        }

        // Track playback
        setCurrentTime(vid.currentTime);
        setIsPlaying(!vid.paused);
      }, 150);
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, findVideoEl, initialAspect, initialCrop]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handlePlayPause = useCallback(() => {
    const vid = findVideoEl();
    if (!vid) return;
    if (vid.paused) vid.play().catch(console.warn);
    else vid.pause();
  }, [findVideoEl]);

  const handleSeek = useCallback((value: number) => {
    setCurrentTime(value);
    const vid = findVideoEl();
    if (vid) vid.currentTime = value;
  }, [findVideoEl]);

  // Computed values
  const effectiveAspect = selectedAspect ?? 16 / 9;
  const effectiveDuration = videoDuration || detectedDuration;
  const matchingPreset = ASPECT_PRESETS.find(p => Math.abs(p.value - effectiveAspect) < 0.01);
  const isNativeSelected = nativeAspect != null && Math.abs(effectiveAspect - nativeAspect) < 0.01;
  const currentAspectLabel = matchingPreset?.label || (isNativeSelected ? 'Original' : 'Custom');

  const handleApply = useCallback(() => {
    if (croppedAreaPixels) {
      onCropComplete({
        x: croppedAreaPixels.x,
        y: croppedAreaPixels.y,
        width: croppedAreaPixels.width,
        height: croppedAreaPixels.height,
        aspect: effectiveAspect,
      }, crop, zoom);
      onClose();
    }
  }, [croppedAreaPixels, onCropComplete, onClose, crop, zoom, effectiveAspect]);

  const handleReset = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (nativeAspect != null) {
      setSelectedAspect(nativeAspect);
    }
  }, [nativeAspect]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-background border-border p-0 overflow-hidden text-foreground shadow-2xl">
        <DialogHeader className="p-6 border-b border-border flex flex-row items-center justify-between">
          <div className="space-y-1">
            <DialogTitle className="text-xl font-bold flex items-center gap-2 tracking-tight">
              <Maximize2 className="h-5 w-5 text-primary" />
              Video Cropper
            </DialogTitle>
            <p className="text-[10px] text-muted-foreground uppercase font-black tracking-[0.2em]">
              {videoReady ? `${currentAspectLabel} Aspect Ratio` : 'Loading...'}
            </p>
          </div>
        </DialogHeader>

        <div className="p-6 space-y-6">
          <div
            ref={cropperContainerRef}
            className="relative w-full h-[450px] bg-black rounded-2xl overflow-hidden shadow-inner border border-border group"
          >
            {/* Always render the Cropper so the video element exists for polling.
                Hide visually until we've detected native aspect. */}
            <div className={cn("absolute inset-0", !videoReady && "invisible")}>
              <Cropper
                video={videoUrl}
                crop={crop}
                zoom={zoom}
                aspect={effectiveAspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
                showGrid={showVerticalFramer}
                style={{
                  containerStyle: { background: '#000' },
                  mediaStyle: { objectFit: 'contain' },
                  cropAreaStyle: {
                    border: '2px solid hsl(var(--primary) / 0.5)',
                    boxShadow: '0 0 0 9999em rgba(0, 0, 0, 0.7)'
                  }
                }}
              />
            </div>

            {!videoReady && (
              <div className="absolute inset-0 flex flex-col items-center justify-center space-y-4">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Loading Video Engine...</span>
              </div>
            )}
          </div>

          <div className="bg-muted/50 p-6 rounded-3xl border border-border space-y-6">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-1 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Maximize2 className="h-3 w-3" /> Zoom Level
                  </Label>
                  <span className="text-xs font-bold text-primary">{zoom.toFixed(2)}x</span>
                </div>
                <Slider
                  value={[zoom]}
                  min={1}
                  max={3}
                  step={0.01}
                  onValueChange={([v]) => setZoom(v)}
                  className="[&>span]:bg-primary"
                />
              </div>

              <div className="flex-1 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Play className="h-3 w-3" /> Timeline Position
                  </Label>
                  <span className="text-xs font-bold text-muted-foreground">
                    {formatTime(currentTime)} / {formatTime(effectiveDuration)}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePlayPause}
                    className="h-10 w-10 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all"
                  >
                    {isPlaying ? <Pause className="h-5 w-5" fill="currentColor" /> : <Play className="h-5 w-5" fill="currentColor" />}
                  </Button>
                  <div className="flex-1">
                    <Slider
                      value={[currentTime]}
                      min={0}
                      max={effectiveDuration || 1}
                      step={0.1}
                      onValueChange={([v]) => handleSeek(v)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator className="bg-card" />

            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-background rounded-full border border-border p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleReset}
                    className="rounded-full text-[10px] font-black uppercase tracking-widest text-amber-500 hover:bg-amber-500/10"
                  >
                    <RotateCcw className="h-3 w-3 mr-2" /> Reset
                  </Button>
                  <Separator orientation="vertical" className="h-4 bg-card mx-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowVerticalFramer(!showVerticalFramer)}
                    className={cn(
                      "rounded-full text-[10px] font-black uppercase tracking-widest transition-colors",
                      showVerticalFramer ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {showVerticalFramer ? <Eye className="h-3 w-3 mr-2" /> : <EyeOff className="h-3 w-3 mr-2" />}
                    Guides
                  </Button>
                </div>

                <div className="flex items-center bg-background rounded-full border border-border p-1 gap-0.5">
                  {ASPECT_PRESETS.map((preset) => (
                    <Button
                      key={preset.label}
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedAspect(preset.value); setCrop({ x: 0, y: 0 }); }}
                      className={cn(
                        "rounded-full text-[10px] font-black uppercase tracking-widest transition-colors px-3",
                        matchingPreset?.label === preset.label
                          ? "text-primary bg-primary/10"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button variant="ghost" onClick={onClose} className="text-muted-foreground font-bold uppercase text-xs tracking-widest">
                  Cancel
                </Button>
                <Button
                  onClick={handleApply}
                  className="bg-primary hover:bg-primary/90 text-white font-black h-11 px-8 rounded-xl shadow-lg shadow-primary/20 active:scale-[0.98] transition-all"
                >
                  <Check className="mr-2 h-4 w-4" strokeWidth={3} />
                  SAVE CROP
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
